import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { SessionService } from '../services/session.service';
import { RestService } from '../services/rest.service';
import { TranslationService } from '../services/translation.service';

interface PurposeOption {
  id: string;
  title: string;
  description: string;
  selected: boolean;
}

@Component({
  selector: 'app-purpose',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
  ],
  templateUrl: './purpose.component.html',
  styleUrls: ['./purpose.component.scss'],
})
export class PurposeComponent {
  purposeOptions: PurposeOption[] = [];
  title = '';
  proceedText = '';
  proceedingText = '';
  busy = false;

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private restService: RestService,
    private translationService: TranslationService
  ) {
    this.initializeTranslations();
    this.subscribeToLanguageChanges();
  }

  private initializeTranslations(): void {
    this.updateTranslations();
  }

  private subscribeToLanguageChanges(): void {
    this.translationService.currentLanguage$.subscribe(() => {
      this.updateTranslations();
    });
  }

  private updateTranslations(): void {
    this.title = this.translationService.translate('purpose.title');
    this.proceedText = this.translationService.translate('purpose.proceed');
    this.proceedingText = this.translationService.translate('common.loading');
    
    const currentSelections = new Map(
      this.purposeOptions.map(opt => [opt.id, opt.selected])
    );

    this.purposeOptions = [
      {
        id: 'no-symptoms',
        title: this.translationService.translate('purpose.options.noSymptoms.title'),
        description: this.translationService.translate('purpose.options.noSymptoms.description'),
        selected: currentSelections.get('no-symptoms') || false,
      },
      {
        id: 'have-symptoms',
        title: this.translationService.translate('purpose.options.haveSymptoms.title'),
        description: this.translationService.translate('purpose.options.haveSymptoms.description'),
        selected: currentSelections.get('have-symptoms') || false,
      },
      {
        id: 'in-treatment',
        title: this.translationService.translate('purpose.options.inTreatment.title'),
        description: this.translationService.translate('purpose.options.inTreatment.description'),
        selected: currentSelections.get('in-treatment') || false,
      },
    ];
  }

  onOptionChange(option: PurposeOption) {
    this.purposeOptions.forEach((opt) => {
      if (opt.id !== option.id) {
        opt.selected = false;
      }
    });

    sessionStorage.setItem('purpose', option.id);
  }

  canProceed(): boolean {
    return this.purposeOptions.some((option) => option.selected);
  }

  getSelectedOption(): PurposeOption | undefined {
    return this.purposeOptions.find((option) => option.selected);
  }

  async proceed() {
    if (this.busy) return;
    const selectedOption = this.getSelectedOption();

    if (!selectedOption) {
      console.error('PurposeComponent: no option selected, cannot proceed');
      return;
    }

    this.busy = true;
    try {
      if (this.sessionService.session) {
        try {
          await this.restService.request({
            path: `user/update-purpose`,
            method: 'POST',
            body: {
              purpose: selectedOption.id,
            },
            throwError: true,
          });

          const session = this.sessionService.session;
          session.user.purpose = selectedOption.id;
          this.sessionService.saveSession(session);
        } catch (error) {
          console.error('PurposeComponent: error updating purpose:', error);
        }
      }

      this.router.navigate(['/app/new-test']);
    } finally {
      this.busy = false;
    }
  }

  onOptionChangButton(option: PurposeOption) {
    const selectedOption = this.purposeOptions.find(
      (opt) => opt.id === option.id
    )!;
    selectedOption.selected = !selectedOption.selected;

    if (selectedOption.selected) {
      this.onOptionChange(option);
    }
  }

  goBack() {
    this.router.navigate(['/app/onboarding']);
  }
}
